import { formatPhoneNumber as formatPhoneNumberInput } from 'react-phone-number-input';
import { z } from 'zod';

import { CARAT_MULTIPLIER } from '../../constants/pestoConstants';
import { formatDate } from '../dateHelper';
import { startCase } from '../string/stringHelper';

export interface JsonObject {
  [key: string]: string | number;
}

export const decimalStringToFormattedDollars = (decimalString: string | null | undefined) => {
  if (!decimalString) return '-';
  const cents = Number(decimalString) * 100;

  return centsToFormattedDollars(cents);
};

export const centsToFormattedDollars = (cents: number | null | undefined) =>
  cents || cents == 0 ? (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : undefined;

export function formatPhoneNumber(phoneNumberString: string) {
  return formatPhoneNumberInput(phoneNumberString);
}

type DetailLeaf = string | number | boolean | null;

type DetailLine = [key: string, value: DetailLeaf];

const toDetailLines = (entries: [string, DetailLeaf | DetailLine[]][]): DetailLine[] =>
  entries.flatMap(([key, value]): DetailLine[] => (Array.isArray(value) ? value : [[key, value]]));

const detailLinesSchema: z.ZodType<DetailLine[], z.ZodTypeDef, unknown> = z.lazy(() => {
  const detailValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null(), detailLinesSchema]);

  return z
    .union([z.array(detailValueSchema), z.record(detailValueSchema)])
    .transform((details) => toDetailLines(Object.entries(details)));
});

const formatKey = (key: string, value: DetailLeaf): string => {
  const stringValue = startCase(value?.toString());
  const stringKey = startCase(key.toString());
  const numberValue = Number(value);

  const formattedValueByKey = new Map([
    ['brand', stringValue.replace(/_/g, ' ')],
    ['color', `${stringValue} color`],
    ['clarity', `${stringValue} clarity`],
    ['length_in_inches', `${stringValue}"`],
    ['carat', `${numberValue / CARAT_MULTIPLIER} ct`],
  ]);

  return `${stringKey}: ${formattedValueByKey.get(key) || stringValue}`;
};

export const parseDetails = (detailsJSON: string): string => {
  const parsedDetails = detailLinesSchema.safeParse(JSON.parse(detailsJSON));
  const detailLines = parsedDetails.success ? parsedDetails.data : [];

  return detailLines
    .map(([key, value]) => `${formatKey(key, value)} |`)
    .join(' ')
    .slice(0, -2);
};

export function parseStringProperties(key: string, value: string) {
  switch (key) {
    case 'updatedAt':
    case 'insertedAt':
      return formatDate(value);
    case 'phone':
      return formatPhoneNumber(value);
    case 'details':
      return parseDetails(value);
    default:
      return value;
  }
}

export function parseNumberProperties(key: string, value: number) {
  switch (key) {
    case 'creditValue':
    case 'valuation':
      return centsToFormattedDollars(value);
    default:
      return value;
  }
}

export function acceptedKeyValueCheck(key: string, value: any) {
  return (
    key === '__typename' ||
    key === 'creditChecks' ||
    key === 'photoLinks' ||
    key === 'presignedPhotoLinks' ||
    key === 'items' ||
    value === null ||
    value === undefined
  );
}
